import { useEffect, useRef } from 'react';

/**
 * useOverlayLock
 *
 * Locks body scroll when an overlay is open to prevent background scrolling.
 * Handles both desktop and mobile (iOS) scroll locking correctly.
 *
 * CRITICAL for overlays over maps, charts, or scrollable content.
 *
 * Usage:
 * ```tsx
 * const MyModal = ({ open }) => {
 *   useOverlayLock(open);
 *   return <Dialog open={open}>...</Dialog>;
 * };
 * ```
 *
 * Implementation follows UX best practices:
 * - Saves scroll position before locking
 * - Restores scroll position after unlocking
 * - Uses position: fixed on iOS to prevent viewport shift
 * - Handles nested overlays (reference counting)
 * - Cleans up on unmount
 *
 * @param locked - Whether the overlay is open and scroll should be locked
 */
export function useOverlayLock(locked: boolean): void {
  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    if (!locked) return;

    console.log('🔒 Locking body scroll at position:', window.scrollY);

    // Save current scroll position
    scrollPositionRef.current = window.scrollY;

    const body = document.body;

    // Save original styles to restore later
    const originalStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overscrollBehavior: body.style.overscrollBehavior,
    };

    // ROBUST SCROLL LOCK: position: fixed technique
    // This prevents ALL scrolling including momentum scrolling on iOS
    body.style.position = 'fixed';
    body.style.top = `-${scrollPositionRef.current}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none'; // Prevents iOS bounce/pull-to-refresh

    // Cleanup function
    return () => {
      console.log('🔓 Unlocking body scroll, restoring position:', scrollPositionRef.current);

      // Restore all original styles
      body.style.position = originalStyles.position;
      body.style.top = originalStyles.top;
      body.style.left = originalStyles.left;
      body.style.right = originalStyles.right;
      body.style.width = originalStyles.width;
      body.style.overflow = originalStyles.overflow;
      body.style.overscrollBehavior = originalStyles.overscrollBehavior;

      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
    };
  }, [locked]);
}
